import { WORKFLOW_SYSTEM_SCHEMA_STATEMENTS } from "./workflow-runner.js";
import { OAUTH_EXCHANGE_SYSTEM_SCHEMA_STATEMENTS } from "./auth-oauth-exchange-schema.js";
import { RAG_SYSTEM_SCHEMA_STATEMENTS } from "./rag-ingest-schema.js";
import { ensureTemplateMarketplacePlatformSchema } from "./server-template-marketplace-bootstrap.js";
import { ensurePlatformEmailSchema } from "./server-platform-email-bootstrap.js";
import { ensurePlatformWebhookSchema } from "./server-platform-webhook-bootstrap.js";
import { ensurePlatformRlsPolicies } from "./server-platform-rls.js";
import { ensureProviderRateLimitAdmissionSchema } from "./server-provider-rate-limit-bootstrap.js";
import { ensureServiceCreditGrantPolicySchema } from "./server-service-credit-grants-bootstrap.js";
import { ensurePlatformAiStreamSessionsSchema } from "./server-platform-ai-stream-bootstrap.js";
import { ensurePlatformAnalyticsSchema } from "./server-platform-analytics-bootstrap.js";

export { ensurePlatformAiStreamSessionsSchema } from "./server-platform-ai-stream-bootstrap.js";
export { ensureProviderRateLimitAdmissionSchema } from "./server-provider-rate-limit-bootstrap.js";
export { ensureServiceCreditGrantPolicySchema } from "./server-service-credit-grants-bootstrap.js";

type RawSql = (query: string, params?: unknown[]) => Promise<unknown>;
type LoggerLike = Pick<Console, "log" | "warn" | "error">;

const MARKUP_DEFAULTS: Record<string, { serviceMarkup: number; platformMarkup: number }> = {
  free: { serviceMarkup: 1.5, platformMarkup: 3.0 },
  pro: { serviceMarkup: 1.0, platformMarkup: 1.5 },
  scale: { serviceMarkup: 0.8, platformMarkup: 1.0 },
};

const IMAGE_DEFAULTS: Record<string, Record<string, unknown>> = {
  free: {
    imageAutoWebp: true,
    imageAutoMaxWidth: 1920,
    imageResize: false,
    imageFormats: ["webp"],
    imageQuality: false,
    imageCacheMaxMB: 200,
    imageTransformsPerMonth: 1000,
  },
  pro: {
    imageAutoWebp: true,
    imageAutoMaxWidth: 3840,
    imageResize: true,
    imageFormats: ["webp", "avif", "jpeg", "png"],
    imageQuality: true,
    imageCacheMaxMB: 1024,
    imageTransformsPerMonth: 50000,
  },
  scale: {
    imageAutoWebp: true,
    imageResize: true,
    imageFormats: ["webp", "avif", "jpeg", "png"],
    imageQuality: true,
    imageCacheMaxMB: 5120,
    imageTransformsPerMonth: -1,
  },
};

const PRICING_TIER_POLICY: Record<
  string,
  { displayName: string; maxApps: number; wsConnections: number; customDomain: boolean }
> = {
  free: { displayName: "Hobby", maxApps: 5, wsConnections: 50, customDomain: false },
  pro: { displayName: "Legacy Paid", maxApps: 10, wsConnections: 500, customDomain: true },
  scale: { displayName: "Startup", maxApps: 50, wsConnections: 5000, customDomain: true },
};

function rowsFromResult<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export function mergeMissingTierFeatureDefaults(
  rawFeatures: string | Record<string, unknown> | null | undefined,
  defaults: Record<string, unknown>,
): { changed: boolean; features: Record<string, unknown> } {
  let features: Record<string, unknown> = {};
  if (typeof rawFeatures === "string" && rawFeatures.trim()) {
    try {
      features = JSON.parse(rawFeatures);
    } catch {
      features = {};
    }
  } else if (rawFeatures && typeof rawFeatures === "object") {
    features = { ...rawFeatures };
  }

  let changed = false;
  for (const [key, value] of Object.entries(defaults)) {
    if (features[key] !== undefined) continue;
    features[key] = value;
    changed = true;
  }
  return { changed, features };
}

export function mergePricingTierFeatureOverrides(
  rawFeatures: string | Record<string, unknown> | null | undefined,
  overrides: Record<string, unknown>,
): { changed: boolean; features: Record<string, unknown> } {
  let features: Record<string, unknown> = {};
  if (typeof rawFeatures === "string" && rawFeatures.trim()) {
    try {
      features = JSON.parse(rawFeatures);
    } catch {
      features = {};
    }
  } else if (rawFeatures && typeof rawFeatures === "object") {
    features = { ...rawFeatures };
  }

  let changed = false;
  for (const [key, value] of Object.entries(overrides)) {
    if (features[key] === value) continue;
    features[key] = value;
    changed = true;
  }
  return { changed, features };
}

export async function ensureWorkflowSystemSchema(params: {
  rawSql: RawSql;
  isBaas: boolean;
  logger?: LoggerLike;
}): Promise<void> {
  const logger = params.logger ?? console;
  if (params.isBaas) {
    logger.log("[workflow] system table bootstrap skipped (BaaS runtime; provisioner-managed)");
    return;
  }

  try {
    for (const stmt of WORKFLOW_SYSTEM_SCHEMA_STATEMENTS) {
      await params.rawSql(stmt);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[workflow] Failed to ensure system tables:", message);
    throw error;
  }
}

export async function ensureOAuthExchangeSystemSchema(params: {
  rawSql: RawSql;
  isBaas: boolean;
  logger?: LoggerLike;
}): Promise<void> {
  const logger = params.logger ?? console;
  if (params.isBaas) {
    logger.log("[auth] OAuth exchange system table bootstrap skipped (BaaS runtime; provisioner-managed)");
    return;
  }

  try {
    for (const stmt of OAUTH_EXCHANGE_SYSTEM_SCHEMA_STATEMENTS) {
      await params.rawSql(stmt);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[auth] Failed to ensure OAuth exchange system table:", message);
    throw error;
  }
}

export async function ensurePlatformNodeSchema(params: { rawSql: RawSql }): Promise<void> {
  await params.rawSql(`CREATE TABLE IF NOT EXISTS platform_nodes (
            id TEXT PRIMARY KEY,
            env TEXT NOT NULL DEFAULT 'prod',
            region TEXT NOT NULL DEFAULT 'kr',
            internal_origin TEXT NOT NULL,
            status TEXT NOT NULL DEFAULT 'active',
            active_port INTEGER NOT NULL DEFAULT 4100,
            routing_weight INTEGER NOT NULL DEFAULT 100,
            max_apps INTEGER NOT NULL DEFAULT 100,
            max_running INTEGER NOT NULL DEFAULT 900,
            current_apps INTEGER NOT NULL DEFAULT 0,
            last_heartbeat_at TIMESTAMPTZ,
            last_ready_at TIMESTAMPTZ,
            capabilities_json TEXT NOT NULL DEFAULT '{}',
            created_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
            updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
        )`);
  await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS node_id TEXT NOT NULL DEFAULT 'node-1'`);
  await params.rawSql(
    `ALTER TABLE apps ADD COLUMN IF NOT EXISTS node_assigned_at TIMESTAMPTZ NOT NULL DEFAULT NOW()`,
  );
  await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS async_http_route_manifest_json TEXT`);
  await params.rawSql(
    `ALTER TABLE apps ADD COLUMN IF NOT EXISTS async_http_route_manifest_updated_at TIMESTAMPTZ`,
  );
  await params.rawSql(`CREATE INDEX IF NOT EXISTS idx_apps_node_id ON apps (node_id)`);
  await params.rawSql(
    `CREATE INDEX IF NOT EXISTS idx_platform_nodes_status_ready ON platform_nodes (status, last_ready_at DESC)`,
  );
}

export async function ensureRagSystemSchema(params: {
  rawSql: RawSql;
  isBaas: boolean;
  logger?: LoggerLike;
}): Promise<void> {
  const logger = params.logger ?? console;
  if (params.isBaas) {
    logger.log("[rag] system table bootstrap skipped (BaaS runtime; provisioner-managed)");
    return;
  }

  try {
    for (const stmt of RAG_SYSTEM_SCHEMA_STATEMENTS) {
      await params.rawSql(stmt);
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    logger.error("[rag] Failed to ensure ingest tables:", message);
    throw error;
  }
}

export async function ensureClosedBetaInviteSeeds(params: {
  rawSql: RawSql;
  logger?: LoggerLike;
}): Promise<void> {
  const logger = params.logger ?? console;

  await params.rawSql(`CREATE TABLE IF NOT EXISTS invite_codes (
            id SERIAL PRIMARY KEY,
            code TEXT NOT NULL UNIQUE,
            max_uses INTEGER NOT NULL DEFAULT 1,
            used_count INTEGER NOT NULL DEFAULT 0,
            created_by TEXT,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
  await params.rawSql(`INSERT INTO invite_codes (code, max_uses, used_count, created_by)
            VALUES ('GENCOW-BETA-2', 100, 0, 'system:closed-beta')
            ON CONFLICT (code) DO UPDATE SET
              max_uses = GREATEST(invite_codes.max_uses, EXCLUDED.max_uses)`);
  await params.rawSql(`CREATE TABLE IF NOT EXISTS beta_invite_waves (
            id SERIAL PRIMARY KEY,
            wave_key TEXT NOT NULL UNIQUE,
            label_ko TEXT NOT NULL,
            label_en TEXT NOT NULL,
            status TEXT NOT NULL CHECK (status IN ('open', 'closed', 'scheduled')),
            public_code TEXT,
            planned_max_uses INTEGER NOT NULL DEFAULT 0,
            opens_at TIMESTAMPTZ,
            sort_order INTEGER NOT NULL,
            is_public BOOLEAN NOT NULL DEFAULT TRUE
        )`);
  await params.rawSql(`INSERT INTO beta_invite_waves
            (wave_key, label_ko, label_en, status, public_code, planned_max_uses, opens_at, sort_order, is_public)
            VALUES
              ('wave-1', '1차 초대', 'Wave 1', 'closed', 'GENCOW-BETA-1', 50, NULL, 1, TRUE),
              ('wave-2', '2차 초대', 'Wave 2', 'open', 'GENCOW-BETA-2', 100, NULL, 2, TRUE),
              ('wave-3', '3차 초대', 'Wave 3', 'scheduled', NULL, 100, '2026-06-10 01:00:00+09', 3, TRUE)
            ON CONFLICT (wave_key) DO NOTHING`);

  logger.log("[db] closed beta invite seeds ✓");
}

async function mergeTierDefaults(params: {
  rawSql: RawSql;
  tierId: string;
  defaults: Record<string, unknown>;
  warningLabel: string;
  logger: LoggerLike;
}): Promise<void> {
  try {
    const tierRows = await params.rawSql(`SELECT features FROM pricing_tiers WHERE id = $1`, [params.tierId]);
    const rows = rowsFromResult<{ features?: string }>(tierRows);
    if (rows.length === 0) return;

    const { features, changed } = mergeMissingTierFeatureDefaults(rows[0]?.features, params.defaults);
    if (!changed) return;

    await params.rawSql(`UPDATE pricing_tiers SET features = $1 WHERE id = $2`, [
      JSON.stringify(features),
      params.tierId,
    ]);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    params.logger.warn(
      `[db] ${params.warningLabel} merge warning for tier=${params.tierId}:`,
      message.slice(0, 100),
    );
  }
}

async function reconcilePricingTierPolicy(params: { rawSql: RawSql; logger: LoggerLike }): Promise<void> {
  for (const [tierId, policy] of Object.entries(PRICING_TIER_POLICY)) {
    try {
      const result = await params.rawSql(
        `SELECT display_name, max_apps, features FROM pricing_tiers WHERE id = $1`,
        [tierId],
      );
      const rows = rowsFromResult<{
        display_name?: unknown;
        max_apps?: unknown;
        features?: string | Record<string, unknown>;
      }>(result);
      const row = rows[0];
      if (!row) continue;

      const existingMaxApps = Number(row.max_apps);
      const existingDisplayName = typeof row.display_name === "string" ? row.display_name : "";
      const { features, changed: featuresChanged } = mergePricingTierFeatureOverrides(row.features, {
        wsConnections: policy.wsConnections,
        customDomain: policy.customDomain,
      });
      if (
        existingMaxApps === policy.maxApps &&
        existingDisplayName === policy.displayName &&
        !featuresChanged
      ) {
        continue;
      }

      await params.rawSql(
        `UPDATE pricing_tiers SET max_apps = $1, display_name = $2, features = $3, updated_at = NOW() WHERE id = $4`,
        [policy.maxApps, policy.displayName, JSON.stringify(features), tierId],
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      params.logger.warn(
        `[db] pricing tier policy reconcile warning for tier=${tierId}:`,
        message.slice(0, 100),
      );
    }
  }
}

export async function ensurePlatformSchemaUpgrade(params: {
  rawSql: RawSql;
  isPlatform: boolean;
  logger?: LoggerLike;
}): Promise<void> {
  if (!params.isPlatform) return;

  const logger = params.logger ?? console;
  try {
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS display_name TEXT`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS plan TEXT DEFAULT 'free'`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS current_deploy_id INTEGER`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS static_deployed_at TIMESTAMP`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS static_size BIGINT`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS custom_domain TEXT UNIQUE`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS custom_domain_status TEXT`);
    await ensurePlatformAnalyticsSchema({ rawSql: params.rawSql });
    await ensurePlatformAiStreamSessionsSchema({ rawSql: params.rawSql });
    await ensurePlatformNodeSchema({ rawSql: params.rawSql });
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS status_reason TEXT`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS status_message TEXT`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS status_updated_at TIMESTAMP`);
    await params.rawSql(
      `ALTER TABLE apps ADD COLUMN IF NOT EXISTS memory_oom_count INTEGER NOT NULL DEFAULT 0`,
    );
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS memory_oom_window_started_at TIMESTAMP`);
    await params.rawSql(`ALTER TABLE apps ADD COLUMN IF NOT EXISTS memory_oom_last_at TIMESTAMP`);
    await params.rawSql(`ALTER TABLE deployments ADD COLUMN IF NOT EXISTS env TEXT DEFAULT 'dev'`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS app_runtime_events (
            id SERIAL PRIMARY KEY,
            app_id INTEGER NOT NULL,
            app_name TEXT NOT NULL,
            kind TEXT NOT NULL,
            source TEXT NOT NULL,
            severity TEXT NOT NULL DEFAULT 'info',
            status_before TEXT,
            status_after TEXT,
            exit_code INTEGER,
            signal TEXT,
            worker_kind TEXT,
            worker_id TEXT,
            pid INTEGER,
            cgroup_path TEXT,
            memory_limit_bytes BIGINT,
            memory_usage_bytes BIGINT,
            message TEXT,
            metadata_json TEXT NOT NULL DEFAULT '{}',
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS app_runtime_events_app_kind_created_idx ON app_runtime_events (app_id, kind, created_at DESC)`,
    );
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS app_runtime_events_app_created_idx ON app_runtime_events (app_id, created_at DESC)`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS platform_domain_audit_log (
            id SERIAL PRIMARY KEY,
            actor_user_id TEXT NOT NULL,
            app_name TEXT NOT NULL,
            domain TEXT NOT NULL,
            action TEXT NOT NULL,
            policy_kind TEXT NOT NULL,
            metadata_json TEXT NOT NULL DEFAULT '{}',
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_platform_domain_audit_log_actor_created ON platform_domain_audit_log (actor_user_id, created_at DESC)`,
    );
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_platform_domain_audit_log_domain_created ON platform_domain_audit_log (domain, created_at DESC)`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS cli_tokens (
            id SERIAL PRIMARY KEY,
            user_id TEXT NOT NULL,
            token TEXT NOT NULL UNIQUE,
            expires_at TIMESTAMP NOT NULL,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(`ALTER TABLE cli_tokens ADD COLUMN IF NOT EXISTS type TEXT NOT NULL DEFAULT 'cli'`);
    await params.rawSql(`ALTER TABLE cli_tokens ADD COLUMN IF NOT EXISTS app_id INTEGER`);
    await params.rawSql(`ALTER TABLE cli_tokens ADD COLUMN IF NOT EXISTS name TEXT`);
    await params.rawSql(`ALTER TABLE cli_tokens ADD COLUMN IF NOT EXISTS last_used_at TIMESTAMP`);
    await params.rawSql(`ALTER TABLE cli_tokens ADD COLUMN IF NOT EXISTS scopes TEXT DEFAULT 'deploy,env'`);
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS static_bandwidth_bytes BIGINT DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS static_requests INTEGER DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS static_storage_bytes BIGINT DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS image_transform_count INTEGER DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS image_source_bytes BIGINT DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS image_output_bytes BIGINT DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS image_credits REAL DEFAULT 0 NOT NULL`,
    );
    await params.rawSql(`ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS memory_rss_mb REAL`);
    await params.rawSql(
      `ALTER TABLE usage_snapshots ADD COLUMN IF NOT EXISTS memory_credits REAL DEFAULT 0 NOT NULL`,
    );
    await ensureProviderRateLimitAdmissionSchema({ rawSql: params.rawSql });
    await params.rawSql(`CREATE TABLE IF NOT EXISTS user_credits (
            id SERIAL PRIMARY KEY,
            user_id TEXT NOT NULL UNIQUE,
            plan TEXT NOT NULL DEFAULT 'free',
            total_credits REAL NOT NULL DEFAULT 20000,
            used_credits REAL NOT NULL DEFAULT 0,
            spend_cap_enabled BOOLEAN NOT NULL DEFAULT false,
            spend_cap_amount REAL,
            billing_cycle_day INTEGER NOT NULL DEFAULT 1,
            last_reset_at TIMESTAMP,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS service_credit_ledger (
            id SERIAL PRIMARY KEY,
            user_id TEXT NOT NULL,
            type TEXT NOT NULL,
            amount REAL NOT NULL,
            balance_after REAL NOT NULL,
            title TEXT NOT NULL,
            service_type TEXT,
            provider TEXT,
            expires_at TIMESTAMP,
            app_id INTEGER,
            payment_ref TEXT,
            remaining REAL,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS service_credit_balance (
            id SERIAL PRIMARY KEY,
            user_id TEXT NOT NULL UNIQUE,
            balance REAL NOT NULL DEFAULT 0,
            total_granted REAL NOT NULL DEFAULT 0,
            total_used REAL NOT NULL DEFAULT 0,
            total_expired REAL NOT NULL DEFAULT 0,
            spend_cap_enabled BOOLEAN NOT NULL DEFAULT false,
            spend_cap_amount REAL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await ensureServiceCreditGrantPolicySchema({ rawSql: params.rawSql });
    await params.rawSql(`CREATE TABLE IF NOT EXISTS service_pricing (
            id SERIAL PRIMARY KEY,
            service_type TEXT NOT NULL,
            provider TEXT NOT NULL DEFAULT 'default',
            display_name TEXT NOT NULL,
            unit_label TEXT NOT NULL DEFAULT '',
            credit_per_unit REAL NOT NULL,
            active BOOLEAN NOT NULL DEFAULT true,
            sort_order INTEGER NOT NULL DEFAULT 0,
            notes TEXT,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL,
            CONSTRAINT service_pricing_type_provider_unique UNIQUE (service_type, provider)
        )`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS service_usage_events (
            id SERIAL PRIMARY KEY,
            user_id TEXT NOT NULL,
            app_id INTEGER,
            service_type TEXT NOT NULL,
            provider TEXT NOT NULL DEFAULT 'default',
            unit_quantity REAL NOT NULL,
            unit_label TEXT NOT NULL DEFAULT '',
            charged_credits REAL NOT NULL,
            source_bytes BIGINT NOT NULL DEFAULT 0,
            output_bytes BIGINT NOT NULL DEFAULT 0,
            request_id TEXT,
            metadata TEXT,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_service_usage_events_user_created ON service_usage_events (user_id, created_at DESC)`,
    );
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_service_usage_events_app_created ON service_usage_events (app_id, created_at DESC)`,
    );
    await params.rawSql(`INSERT INTO service_pricing (service_type, provider, display_name, unit_label, credit_per_unit, active, sort_order, notes)
            VALUES
                ('document_convert', 'default', 'Document Convert', 'MB', 1, true, 0, 'Fallback document conversion rate'),
                ('document_convert', 'kordoc', 'Document Convert · kordoc', 'MB', 1, true, 5, NULL),
                ('document_convert', 'openai', 'Document Convert · OpenAI', 'MB', 1, true, 10, NULL),
                ('document_convert', 'gemini', 'Document Convert · Gemini', 'MB', 1, true, 20, NULL),
                ('document_convert', 'ocr', 'Document Convert · OCR', 'MB', 1, true, 30, NULL),
                ('document_convert', 'custom_vlm', 'Document Convert · Custom VLM', 'MB', 1, true, 40, NULL),
                ('document_convert', 'opendataloader', 'Document Convert · OpenDataLoader', 'MB', 1, true, 50, NULL)
            ON CONFLICT (service_type, provider) DO UPDATE SET
                display_name = EXCLUDED.display_name,
                unit_label = EXCLUDED.unit_label,
                active = EXCLUDED.active,
                sort_order = EXCLUDED.sort_order,
                updated_at = NOW()`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS cron_jobs (
            id SERIAL PRIMARY KEY,
            app_id INTEGER NOT NULL,
            name TEXT NOT NULL,
            schedule TEXT NOT NULL,
            action TEXT NOT NULL,
            next_run_at TIMESTAMP NOT NULL,
            last_run_at TIMESTAMP,
            last_status TEXT,
            last_error TEXT,
            status TEXT NOT NULL DEFAULT 'enabled',
            created_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_cron_jobs_poll ON cron_jobs (status, next_run_at) WHERE status = 'enabled'`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS platform_runtime_state (
            key TEXT PRIMARY KEY,
            value TEXT NOT NULL,
            updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW()
        )`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS scheduled_jobs (
            id TEXT PRIMARY KEY,
            app_id INTEGER NOT NULL,
            action TEXT NOT NULL,
            args JSONB DEFAULT '{}',
            run_at TIMESTAMPTZ NOT NULL,
            status TEXT NOT NULL DEFAULT 'pending',
            error TEXT,
            on_error_action TEXT,
            retry_count INTEGER NOT NULL DEFAULT 0,
            max_retries INTEGER NOT NULL DEFAULT 3,
            dedupe_key TEXT,
            created_at TIMESTAMPTZ DEFAULT NOW(),
            updated_at TIMESTAMPTZ DEFAULT NOW()
        )`);
    await params.rawSql(`ALTER TABLE scheduled_jobs ADD COLUMN IF NOT EXISTS dedupe_key TEXT`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_scheduled_jobs_poll ON scheduled_jobs (status, run_at) WHERE status = 'pending'`,
    );
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_scheduled_jobs_app_status ON scheduled_jobs (app_id, status)`,
    );
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_scheduled_jobs_app_status_run_at ON scheduled_jobs (app_id, status, run_at) WHERE status = 'pending'`,
    );
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_scheduled_jobs_app_action_status ON scheduled_jobs (app_id, action, status)`,
    );
    await params.rawSql(
      `CREATE UNIQUE INDEX IF NOT EXISTS idx_scheduled_jobs_active_dedupe ON scheduled_jobs (app_id, action, dedupe_key) WHERE dedupe_key IS NOT NULL AND status IN ('pending', 'running')`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS platform_job_admission_buckets (
            scope TEXT NOT NULL,
            bucket_start TIMESTAMPTZ NOT NULL,
            count INTEGER NOT NULL DEFAULT 0,
            updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
            PRIMARY KEY (scope, bucket_start)
        )`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_platform_job_admission_buckets_updated ON platform_job_admission_buckets (updated_at)`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS cron_job_runs (
            id SERIAL PRIMARY KEY,
            cron_job_id INTEGER NOT NULL,
            cron_name TEXT NOT NULL,
            action TEXT NOT NULL,
            status TEXT NOT NULL,
            error TEXT,
            duration_ms INTEGER,
            started_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
            completed_at TIMESTAMPTZ
        )`);
    await params.rawSql(
      `CREATE INDEX IF NOT EXISTS idx_cron_job_runs_lookup ON cron_job_runs (cron_job_id, started_at DESC)`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS pricing_tiers (
            id TEXT PRIMARY KEY,
            display_name TEXT NOT NULL,
            sort_order INTEGER NOT NULL DEFAULT 0,
            max_apps INTEGER NOT NULL DEFAULT 5,
            monthly_credits REAL NOT NULL DEFAULT 20000,
            max_storage_bytes BIGINT NOT NULL DEFAULT 1073741824,
            features TEXT NOT NULL DEFAULT '{}',
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(`INSERT INTO pricing_tiers (id, display_name, sort_order, max_apps, monthly_credits, max_storage_bytes, features)
            VALUES
                ('free',  'Hobby', 0,  5,  20000,   1073741824, '{"autoBackup":false,"wsConnections":50,"customDomain":false}'),
                ('pro',   'Legacy Paid',   10, 10, 100000,  5368709120, '{"autoBackup":true,"autoBackupIntervalHours":24,"wsConnections":500,"customDomain":true}'),
                ('scale', 'Startup', 20, 50, 500000, 21474836480, '{"autoBackup":true,"autoBackupIntervalHours":6,"wsConnections":5000,"customDomain":true}')
            ON CONFLICT (id) DO NOTHING`);
    await params.rawSql(`CREATE TABLE IF NOT EXISTS model_pricing (
            id SERIAL PRIMARY KEY,
            model TEXT NOT NULL UNIQUE,
            type TEXT NOT NULL,
            provider TEXT NOT NULL DEFAULT 'openai',
            display_name TEXT NOT NULL,
            unit_label TEXT NOT NULL DEFAULT '',
            input_credit_per_token REAL NOT NULL,
            output_credit_per_token REAL NOT NULL DEFAULT 0,
            active BOOLEAN NOT NULL DEFAULT true,
            sort_order INTEGER NOT NULL DEFAULT 0,
            notes TEXT,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL
        )`);
    await params.rawSql(
      `ALTER TABLE model_pricing ADD COLUMN IF NOT EXISTS unit_label TEXT NOT NULL DEFAULT ''`,
    );
    await params.rawSql(`CREATE TABLE IF NOT EXISTS ai_model_substitutions (
            id TEXT PRIMARY KEY,
            operation TEXT NOT NULL DEFAULT 'ai_chat',
            requested_model TEXT NOT NULL,
            effective_model TEXT NOT NULL,
            reason TEXT NOT NULL,
            active BOOLEAN NOT NULL DEFAULT true,
            created_at TIMESTAMP DEFAULT NOW() NOT NULL,
            updated_at TIMESTAMP DEFAULT NOW() NOT NULL,
            UNIQUE(operation, requested_model)
        )`);
    await params.rawSql(`INSERT INTO model_pricing (model, type, provider, display_name, unit_label, input_credit_per_token, output_credit_per_token, active, sort_order)
            VALUES
                ('gpt-5.5',                 'chat',     'openai', 'GPT-5.5',             '1K tok', 50,    300,   false, 0),
                ('gpt-5.4',                 'chat',     'openai', 'GPT-5.4',             '1K tok', 25,    150,   true, 10),
                ('gpt-5.4-mini',            'chat',     'openai', 'GPT-5.4 mini',        '1K tok', 7.5,   45,    true, 20),
                ('gpt-5.4-nano',            'chat',     'openai', 'GPT-5.4 nano',        '1K tok', 2,     12.5,  true, 30),
                ('gpt-5.3-chat-latest',     'chat',     'openai', 'GPT-5.3 Chat',        '1K tok', 17.5,  140,   false, 40),
                ('gpt-5.2',                 'chat',     'openai', 'GPT-5.2',             '1K tok', 17.5,  140,   false, 50),
                ('gpt-5.2-chat-latest',     'chat',     'openai', 'GPT-5.2 Chat',        '1K tok', 17.5,  140,   false, 60),
                ('gpt-5.1',                 'chat',     'openai', 'GPT-5.1',             '1K tok', 12.5,  100,   false, 70),
                ('gpt-5',                   'chat',     'openai', 'GPT-5',               '1K tok', 12.5,  100,   false, 80),
                ('gpt-5-mini',              'chat',     'openai', 'GPT-5 mini',          '1K tok', 2.5,   20,    true, 90),
                ('gpt-5-nano',              'chat',     'openai', 'GPT-5 nano',          '1K tok', 0.5,   4,     true, 100),
                ('gpt-4.1',                 'chat',     'openai', 'GPT-4.1',             '1K tok', 20,    80,    false, 110),
                ('gpt-4.1-mini',            'chat',     'openai', 'GPT-4.1 mini',        '1K tok', 4,     16,    false, 120),
                ('gpt-4.1-nano',            'chat',     'openai', 'GPT-4.1 nano',        '1K tok', 1,     4,     false, 130),
                ('gpt-4o',                  'chat',     'openai', 'GPT-4o',              '1K tok', 25,    100,   false, 140),
                ('gpt-4o-mini',             'chat',     'openai', 'GPT-4o mini',         '1K tok', 1.5,   6,     false, 150),
                ('gpt-image-2',             'image',    'openai', 'GPT Image 2',         '1K tok', 50,    300,   true, 180),
                ('gpt-image-1.5',           'image',    'openai', 'GPT Image 1.5',       '1K tok', 50,    320,   true, 190),
                ('gpt-image-1-mini',        'image',    'openai', 'GPT Image 1 Mini',    '1K tok', 20,    80,    true, 195),
                ('text-embedding-3-small',  'embed',    'openai', 'Embedding 3 Small',   '1K tok', 0.2,   0,     true, 200),
                ('text-embedding-3-large',  'embed',    'openai', 'Embedding 3 Large',   '1K tok', 1.3,   0,     true, 210),
                ('api_calls',               'platform', 'gencow', 'API 호출',            '1K건',   10,    0,     true, 300),
                ('function_ms',             'platform', 'gencow', '함수 실행',            'hr',     400,   0,     true, 310),
                ('realtime_ms',             'platform', 'gencow', '리얼타임 접속',        '1K분',   10,    0,     true, 320),
                ('db_storage_bytes_hr',     'platform', 'gencow', 'DB 스토리지',          'GB·월',  800,   0,     true, 330),
                ('file_storage_bytes_hr',   'platform', 'gencow', '파일 스토리지',        'GB·월',  230,   0,     true, 340),
                ('bandwidth_bytes',         'platform', 'gencow', 'DB 대역폭',            'GB',     900,   0,     true, 350),
                ('static_storage_bytes_hr', 'platform', 'gencow', 'Static 스토리지',      'GB·월',  0.1,   0,     true, 360),
                ('static_bandwidth_bytes',  'platform', 'gencow', 'Static 대역폭',        'GB',     0.5,   0,     true, 370),
                ('static_requests',         'platform', 'gencow', 'Static 요청',          '1M건',   10,    0,     true, 380),
                ('memory_mb_hr',            'platform', 'gencow', '메모리 사용',          'GB·hr',  300,   0,     true, 390),
                ('image_transforms',        'platform', 'gencow', 'Image Transform',      '1K건',   50,    0,     true, 400),
                ('image_source_bytes',      'platform', 'gencow', 'Image 원본 처리',      'GB',     20,    0,     true, 410),
                ('image_output_bytes',      'platform', 'gencow', 'Image 결과 캐시',      'GB',     10,    0,     true, 420)
            ON CONFLICT (model) DO UPDATE SET
                display_name = EXCLUDED.display_name,
                unit_label = EXCLUDED.unit_label,
                input_credit_per_token = EXCLUDED.input_credit_per_token,
                output_credit_per_token = EXCLUDED.output_credit_per_token,
                sort_order = EXCLUDED.sort_order`);
    await params.rawSql(`INSERT INTO ai_model_substitutions (
            id, operation, requested_model, effective_model, reason, active
        ) VALUES
            ('sub_gpt_5_5_to_gpt_5_4', 'ai_chat', 'gpt-5.5', 'gpt-5.4', 'azure_quota_policy', true),
            ('sub_gpt_4o_mini_to_gpt_5_mini', 'ai_chat', 'gpt-4o-mini', 'gpt-5-mini', 'deprecated_model', true)
        ON CONFLICT (operation, requested_model) DO UPDATE SET
            effective_model = EXCLUDED.effective_model,
            reason = EXCLUDED.reason,
            active = EXCLUDED.active,
            updated_at = NOW()`);

    for (const [tierId, defaults] of Object.entries(MARKUP_DEFAULTS)) {
      await mergeTierDefaults({
        rawSql: params.rawSql,
        tierId,
        defaults,
        warningLabel: "markup",
        logger,
      });
    }

    for (const [tierId, defaults] of Object.entries(IMAGE_DEFAULTS)) {
      await mergeTierDefaults({
        rawSql: params.rawSql,
        tierId,
        defaults,
        warningLabel: "image features",
        logger,
      });
    }

    await reconcilePricingTierPolicy({
      rawSql: params.rawSql,
      logger,
    });

    await ensureTemplateMarketplacePlatformSchema({
      rawSql: params.rawSql,
      isPlatform: params.isPlatform,
      logger,
    });
    await ensurePlatformEmailSchema({
      rawSql: params.rawSql,
      isPlatform: params.isPlatform,
      logger,
    });
    await ensurePlatformWebhookSchema({
      rawSql: params.rawSql,
      isPlatform: params.isPlatform,
      logger,
    });
    await ensureClosedBetaInviteSeeds({
      rawSql: params.rawSql,
      logger,
    });

    logger.log("[db] schema upgrade ✓");
  } catch (error) {
    logger.warn("[db] schema upgrade warning:", error instanceof Error ? error.message : String(error));
  }

  await ensurePlatformRlsPolicies({
    rawSql: params.rawSql,
    isPlatform: params.isPlatform,
    logger,
  });
}
